import React from 'react'
import { View, StyleSheet, Image, TouchableOpacity, Linking } from 'react-native'
import { Link, useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { iconApp } from '@assets'
import { Icon, Text, NotifBadge } from '@components'
import { WebSidebarToggle } from './WebSidebarToggle'
import { useScreen } from '../../hooks/useScreen'
import { useUnreadNotificationsCount, useUnreadConversationsCount } from '@hooks'
import { useLanguage } from '@context'

interface WebHeaderProps {
  isCollapsed?: boolean
  isExtension?: boolean
  onToggleSidebar?: () => void
  onSearchPress?: () => void
  onNotificationsPress?: () => void
  onMessagesPress?: () => void
}

export const WebHeader: React.FC<WebHeaderProps> = ({ onSearchPress, onNotificationsPress, onMessagesPress, isCollapsed, isExtension, onToggleSidebar }) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { isMobile } = useScreen()
  const navigation = useNavigation()
  const { t } = useLanguage()
  const { unreadCount } = useUnreadNotificationsCount()
  const { unreadCount: unreadMessagesCount } = useUnreadConversationsCount()

  const navigateToHome = () => {
    if(isExtension){
      Linking.openURL('https://keeptouch.co')
    }
    else {
      (navigation as any).navigate('Tab', {screen: 'ListContacts '})
    }
  }
  return (
    <View style={styles.header}>
      <TouchableOpacity style={styles.headerLeft} onPress={navigateToHome}>
        <Image source={iconApp} style={styles.headerLogo} />
        <Text type={isExtension? "titleBold" : "headline"} color={theme.colors.primary} numberOfLines={1} style={styles.headerTitle}>
          KEEPTOUCH
        </Text>
      </TouchableOpacity>
      <View style={[styles.headerRight, !isMobile && { paddingRight: theme.spacing.md, gap: theme.spacing.xs }]}>
        <TouchableOpacity
          onPress={onMessagesPress}
          activeOpacity={0.7}
          style={styles.headerIconButton}
        >
          <View style={styles.iconWrapper}>
            <Icon name="messageCircle" size={theme.iconSize.small} />
            <NotifBadge total={unreadMessagesCount} testID="header-messages-badge" />
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onNotificationsPress}
          activeOpacity={0.7}
          style={styles.headerIconButton}
        >
          <View style={styles.iconWrapper}>
            <Icon name="bell" size={theme.iconSize.small} />
            <NotifBadge total={unreadCount} testID="header-notifications-badge" />
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onSearchPress}
          activeOpacity={0.7}
          style={styles.headerIconButton}
        >
          <Icon name="search" size={theme.iconSize.small} />
          {!isMobile && <Text type="bodySmall" color={theme.colors.secondary}>
            {t('webNav.search')}
          </Text>}
        </TouchableOpacity>
        {isMobile && !isExtension && onToggleSidebar && (
          <WebSidebarToggle
            isCollapsed={isCollapsed || false}
            onToggle={onToggleSidebar}
            isMobile={isMobile}
          />
        )}
      </View>
    </View>
  )
}

const getStyle = (theme: Theme) => StyleSheet.create({
  header: {
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey,
  },
  headerLogo: {
    width: 30,
    height: 30,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  headerTitle: {
    flexShrink: 1,
  },
  headerRight: {
    flex: 1,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing.md,
  },
  headerIconButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: theme.spacing.xs,
  },
  iconWrapper: {
    position: 'relative',
  },
})
